import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SessionMetadata, SessionTimelineItem, Transcript } from 'api/types/transcript';
import SessionsApi from 'api/sessions';
import type { RootState } from '..';

interface TranscriptState {
  sessionId: string | null;
  items: SessionTimelineItem[];
  firstItemIndex: number;
  oldestCursor: string | null;
  latestCursor: string | null;
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
  initialLoading: boolean;
  loadingOlder: boolean;
  loadingNewer: boolean;
  catchingUp: boolean;
  error?: string;
}

const FIRST_ITEM_INDEX_BASE = 1_000_000;

const initialState: TranscriptState = {
  sessionId: null,
  items: [],
  firstItemIndex: FIRST_ITEM_INDEX_BASE,
  oldestCursor: null,
  latestCursor: null,
  hasMoreOlder: true,
  hasMoreNewer: false,
  initialLoading: false,
  loadingOlder: false,
  loadingNewer: false,
  catchingUp: false,
  error: undefined
};

const getTimelineItemTime = (item: SessionTimelineItem): number => {
  if (item.sortDate) return new Date(item.sortDate).getTime();
  if (item.itemType === 'metadata') return new Date(item.eventDate).getTime();
  return new Date(item.startDate).getTime();
};

const insertSorted = (items: SessionTimelineItem[], newItem: SessionTimelineItem): number => {
  const time = getTimelineItemTime(newItem);
  let lo = 0;
  let hi = items.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (getTimelineItemTime(items[mid]) < time) lo = mid + 1;
    else hi = mid;
  }
  items.splice(lo, 0, newItem);
  return lo;
};

// ── Thunks ──────────────────────────────────────────────────────────

export const fetchInitialTimeline = createAsyncThunk<
  {
    items: SessionTimelineItem[];
    startCursor: string | null;
    endCursor: string | null;
    hasMore: boolean;
    hasMoreNewer: boolean;
  },
  { sessionId: string; limit?: number; around?: string },
  { state: RootState; rejectValue: string }
>(
  'transcript/fetchInitialTimeline',
  async ({ sessionId, limit = 50, around }, { rejectWithValue }) => {
    try {
      const res = await SessionsApi.fetchTimelineCursor(sessionId, {
        limit,
        direction: 'older',
        around
      });
      const { data, pageInfo } = res.data;
      return {
        items: data,
        startCursor: pageInfo.startCursor,
        endCursor: pageInfo.endCursor,
        hasMore: pageInfo.hasMore,
        hasMoreNewer: pageInfo.hasMoreNewer ?? false
      };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to fetch timeline');
    }
  },
  {
    condition: (_, { getState }) => {
      const { initialLoading } = getState().transcripts;
      return !initialLoading;
    }
  }
);

export const fetchOlderTimeline = createAsyncThunk<
  { items: SessionTimelineItem[]; startCursor: string | null; hasMore: boolean },
  { sessionId: string; limit?: number },
  { state: RootState; rejectValue: string }
>(
  'transcript/fetchOlderTimeline',
  async ({ sessionId, limit = 50 }, { getState, rejectWithValue }) => {
    try {
      const { oldestCursor } = getState().transcripts;
      const res = await SessionsApi.fetchTimelineCursor(sessionId, {
        limit,
        cursor: oldestCursor ?? undefined,
        direction: 'older'
      });
      const { data, pageInfo } = res.data;
      return {
        items: data,
        startCursor: pageInfo.startCursor,
        hasMore: pageInfo.hasMore
      };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to fetch older');
    }
  },
  {
    condition: (_, { getState }) => {
      const { loadingOlder, hasMoreOlder } = getState().transcripts;
      return !loadingOlder && hasMoreOlder;
    }
  }
);

/**
 * Pages toward the present after an `around` (deep-link) initial load. Distinct
 * from fetchTimelineCatchUp, which is the live-session reconnect path.
 */
export const fetchNewerTimeline = createAsyncThunk<
  { items: SessionTimelineItem[]; endCursor: string | null; hasMore: boolean },
  { sessionId: string; limit?: number },
  { state: RootState; rejectValue: string }
>(
  'transcript/fetchNewerTimeline',
  async ({ sessionId, limit = 50 }, { getState, rejectWithValue }) => {
    try {
      const { latestCursor } = getState().transcripts;
      const res = await SessionsApi.fetchTimelineCursor(sessionId, {
        limit,
        cursor: latestCursor ?? undefined,
        direction: 'newer'
      });
      const { data, pageInfo } = res.data;
      return {
        items: data,
        endCursor: pageInfo.endCursor,
        hasMore: pageInfo.hasMore
      };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to load newer');
    }
  },
  {
    condition: (_, { getState }) => {
      const { loadingNewer, hasMoreNewer } = getState().transcripts;
      return !loadingNewer && hasMoreNewer;
    }
  }
);

export const fetchTimelineCatchUp = createAsyncThunk<
  { items: SessionTimelineItem[]; endCursor: string | null },
  { sessionId: string; limit?: number },
  { state: RootState; rejectValue: string }
>(
  'transcript/fetchTimelineCatchUp',
  async ({ sessionId, limit = 100 }, { getState, rejectWithValue }) => {
    try {
      const { latestCursor } = getState().transcripts;
      const res = await SessionsApi.fetchTimelineCursor(sessionId, {
        limit,
        cursor: latestCursor ?? undefined,
        direction: 'newer'
      });
      const { data, pageInfo } = res.data;
      return {
        items: data,
        endCursor: pageInfo.endCursor
      };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to catch up');
    }
  },
  {
    condition: (_, { getState }) => {
      const { catchingUp } = getState().transcripts;
      return !catchingUp;
    }
  }
);

// ── Slice ───────────────────────────────────────────────────────────

const transcriptSlice = createSlice({
  name: 'transcript',
  initialState,
  reducers: {
    clearTranscripts: (state) => {
      state.items = [];
      state.firstItemIndex = FIRST_ITEM_INDEX_BASE;
      state.oldestCursor = null;
      state.latestCursor = null;
      state.hasMoreOlder = true;
      state.hasMoreNewer = false;
      state.error = undefined;
    },
    resetTranscriptState: () => initialState,
    setSessionId: (state, action: PayloadAction<string | null>) => {
      if (state.sessionId !== action.payload) {
        Object.assign(state, { ...initialState, sessionId: action.payload });
      }
    },
    addTranscript: (state, action: PayloadAction<Transcript>) => {
      const newItem = { ...action.payload, itemType: 'transcript' as const };
      if (state.items.some((t) => t.id === newItem.id)) return;
      const idx = insertSorted(state.items, newItem);
      if (idx === 0) state.firstItemIndex = Math.max(0, state.firstItemIndex - 1);
    },
    removeTranscript: (state, action: PayloadAction<string>) => {
      const idx = state.items.findIndex(
        (t) => t.itemType !== 'metadata' && t.id === action.payload
      );
      if (idx >= 0) {
        state.items.splice(idx, 1);
        if (idx === 0) state.firstItemIndex += 1;
      }
    },
    updateTranscript: (state, action: PayloadAction<{ id: string; content: string }>) => {
      const item = state.items.find((t) => t.itemType !== 'metadata' && t.id === action.payload.id);
      if (item) item.content = action.payload.content;
    },
    addMetadata: (state, action: PayloadAction<SessionMetadata>) => {
      const newItem = { ...action.payload, itemType: 'metadata' as const };
      if (state.items.some((t) => t.id === newItem.id)) return;
      const idx = insertSorted(state.items, newItem);
      if (idx === 0) state.firstItemIndex = Math.max(0, state.firstItemIndex - 1);
    },
    removeMetadata: (state, action: PayloadAction<string>) => {
      const idx = state.items.findIndex(
        (t) => t.itemType === 'metadata' && t.id === action.payload
      );
      if (idx >= 0) {
        state.items.splice(idx, 1);
        if (idx === 0) state.firstItemIndex += 1;
      }
    },
    // Intentionally does not clear items - avoids flash on live→ended transition
    markSessionEnded: (_state) => {}
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInitialTimeline.pending, (state) => {
        state.initialLoading = true;
        state.error = undefined;
      })
      .addCase(fetchInitialTimeline.fulfilled, (state, action) => {
        state.initialLoading = false;
        state.items = action.payload.items;
        state.oldestCursor = action.payload.startCursor;
        state.latestCursor = action.payload.endCursor;
        state.hasMoreOlder = action.payload.hasMore;
        state.hasMoreNewer = action.payload.hasMoreNewer;
        state.firstItemIndex = FIRST_ITEM_INDEX_BASE;
        state.sessionId = action.meta.arg.sessionId;
      })
      .addCase(fetchInitialTimeline.rejected, (state, action) => {
        state.initialLoading = false;
        state.error = action.payload ?? 'Something went wrong';
      })
      .addCase(fetchOlderTimeline.pending, (state) => {
        state.loadingOlder = true;
      })
      .addCase(fetchOlderTimeline.fulfilled, (state, action) => {
        state.loadingOlder = false;
        const { items: olderItems, startCursor, hasMore } = action.payload;
        if (olderItems.length > 0) {
          state.items = [...olderItems, ...state.items];
          state.firstItemIndex = Math.max(0, state.firstItemIndex - olderItems.length);
        }
        state.oldestCursor = startCursor;
        state.hasMoreOlder = hasMore;
      })
      .addCase(fetchOlderTimeline.rejected, (state, action) => {
        state.loadingOlder = false;
        state.error = action.payload ?? 'Failed to load older';
      })
      .addCase(fetchNewerTimeline.pending, (state) => {
        state.loadingNewer = true;
      })
      .addCase(fetchNewerTimeline.fulfilled, (state, action) => {
        state.loadingNewer = false;
        const { items: newerItems, endCursor, hasMore } = action.payload;
        if (newerItems.length > 0) {
          const existingIds = new Set(state.items.map((t) => t.id));
          state.items = [...state.items, ...newerItems.filter((t) => !existingIds.has(t.id))];
        }
        if (endCursor) state.latestCursor = endCursor;
        state.hasMoreNewer = hasMore;
      })
      .addCase(fetchNewerTimeline.rejected, (state, action) => {
        state.loadingNewer = false;
        state.error = action.payload ?? 'Failed to load newer';
      })
      .addCase(fetchTimelineCatchUp.pending, (state) => {
        state.catchingUp = true;
      })
      .addCase(fetchTimelineCatchUp.fulfilled, (state, action) => {
        state.catchingUp = false;
        const { items: newerItems, endCursor } = action.payload;
        if (newerItems.length > 0) {
          const existingIds = new Set(state.items.map((t) => t.id));
          const deduped = newerItems.filter((t) => !existingIds.has(t.id));
          for (const item of deduped) {
            insertSorted(state.items, item);
          }
        }
        if (endCursor) state.latestCursor = endCursor;
      })
      .addCase(fetchTimelineCatchUp.rejected, (state, action) => {
        state.catchingUp = false;
        state.error = action.payload ?? 'Failed to catch up';
      });
  }
});

export const {
  clearTranscripts,
  resetTranscriptState,
  setSessionId,
  addTranscript,
  removeTranscript,
  updateTranscript,
  addMetadata,
  removeMetadata,
  markSessionEnded
} = transcriptSlice.actions;
export default transcriptSlice.reducer;
